/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {isDOMNode} from 'lexical';
import * as React from 'react';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

const DropDownContext = React.createContext();

// Global counter to track how many dropdowns are currently open
let openDropdownCount = 0;
let originalBodyOverflow = '';

const dropDownPadding = 4;

export function DropDownItem({
  children,
  className,
  onClick,
  title,
}) {
  const ref = useRef(null);

  const dropDownContext = React.useContext(DropDownContext);

  if (dropDownContext === null) {
    throw new Error('DropDownItem must be used within a DropDown');
  }

  const {registerItem} = dropDownContext;

  useEffect(() => {
    if (ref && ref.current) {
      registerItem(ref);
    }
  }, [ref, registerItem]);

  return (
    <button
      className={className}
      onClick={onClick}
      ref={ref}
      title={title}
      type="button">
      {children}
    </button>
  );
}

function DropDownItems({
  children,
  dropDownRef,
  onClose,
}) {
  const [items, setItems] = useState([]);
  const [highlightedItem, setHighlightedItem] = useState(null);

  const registerItem = useCallback(
    (itemRef) => {
      setItems((prev) => ([...prev, itemRef]));
    },
    [setItems],
  );

  const handleKeyDown = (event) => {
    if (!items) {
      return;
    }

    const key = event.key;

    if (['Escape', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
      event.preventDefault();
    }

    if (key === 'Escape' || key === 'Tab') {
      onClose();
    } else if (key === 'ArrowUp') {
      setHighlightedItem((prev) => {
        if (!prev) {
          return items[0];
        }
        const index = items.indexOf(prev) - 1;
        return items[index === -1 ? items.length - 1 : index];
      });
    } else if (key === 'ArrowDown') {
      setHighlightedItem((prev) => {
        if (!prev) {
          return items[0];
        }
        return items[items.indexOf(prev) + 1];
      });
    }
  };

  const contextValue = useMemo(
    () => ({
      registerItem,
    }),
    [registerItem],
  );

  useEffect(() => {
    if (items.length > 0 && !highlightedItem) {
      setHighlightedItem(items[0]);
    }
    if (highlightedItem && highlightedItem.current) {
      highlightedItem.current.focus();
      // Scroll the highlighted item into view if dropdown is scrollable
      highlightedItem.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [items, highlightedItem]);

  return (
    <DropDownContext.Provider value={contextValue}>
      <div 
        className="dropdown" 
        ref={dropDownRef} 
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          zIndex: 1200, // Higher than modal's z-index (1100) to appear above modals
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          // Size to content - updateDropdownPosition will set explicit height for scrollable dropdowns
          minHeight: 'auto'
        }}>
        {children}
      </div>
    </DropDownContext.Provider>
  );
}

export default function DropDown({
  disabled = false,
  buttonLabel,
  buttonAriaLabel,
  buttonClassName,
  buttonIconClassName,
  children,
  stopCloseOnClickSelf,
  preventBodyScroll = false, /* When false, keep page scrollbar visible (e.g. toolbar dropdowns) */
}){
  const dropDownRef = useRef(null);
  const buttonRef = useRef(null);
  const [showDropDown, setShowDropDown] = useState(false);

  const handleClose = () => {
    setShowDropDown(false);
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  // Helper function to determine dropdown position and dimensions
  const getDropdownPosition = (button, dropdown) => {
    const buttonRect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    
    // Calculate available space below and above the button
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    // Minimum height is 500px, but dropdown can be smaller if content is less
    const minHeight = 500;
    const naturalHeight = dropdown.scrollHeight;
    
    // For small dropdowns (like column layout options), prefer opening downward
    // Only open upward if there's really not enough space below (less than 100px)
    const isSmallDropdown = naturalHeight < 300;
    const minimumSpaceBelow = isSmallDropdown ? 100 : minHeight;
    
    // Determine if we should open upward
    // For small dropdowns, only open upward if there's less than minimumSpaceBelow below
    // For larger dropdowns, use the original logic
    // Also ensure there's actually more space above before opening upward
    const shouldOpenUpward = spaceBelow < minimumSpaceBelow && spaceAbove > spaceBelow + 50;
    
    // Calculate available space for the dropdown (always use space below)
    const availableSpace = spaceBelow;
    
    // Determine final height
    let finalHeight;
    const isCompactDropdown = naturalHeight < 150; // e.g. Vertical Align with 3 items
    if (isCompactDropdown) {
      // For small dropdowns, use natural height - don't force minimum (avoids overlapping menu items below)
      finalHeight = naturalHeight;
    } else if (naturalHeight <= minHeight) {
      // If content is less than minHeight, use natural height but respect available space
      finalHeight = Math.min(naturalHeight, availableSpace - dropDownPadding * 2);
    } else {
      // If content exceeds minHeight, use minHeight but respect available space
      finalHeight = Math.min(minHeight, availableSpace - dropDownPadding * 2);
    }
    
    // Ensure minimum only for larger dropdowns (small ones use natural height)
    if (!isCompactDropdown) {
      finalHeight = Math.max(finalHeight, 200);
    }
    
    return {
      shouldOpenUpward,
      spaceBelow,
      spaceAbove,
      finalHeight,
      needsScroll: naturalHeight > finalHeight
    };
  };

  const updateDropdownPosition = useCallback(() => {
    const button = buttonRef.current;
    const dropdown = dropDownRef.current;

    if (button && dropdown) {
      const buttonRect = button.getBoundingClientRect();
      let {shouldOpenUpward, finalHeight, needsScroll} = getDropdownPosition(button, dropdown);
      
      // Check if button is inside a modal - if so, always open downward
      let parent = button.parentElement;
      let isInsideModal = false;
      while (parent && parent !== document.body) {
        if (parent.classList.contains('Modal__modal') || parent.classList.contains('Modal__overlay')) {
          isInsideModal = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (isInsideModal) {
        shouldOpenUpward = false;
      }
      
      // Set dropdown dimensions and scroll behavior
      dropdown.style.height = `${finalHeight}px`;
      dropdown.style.minHeight = `${Math.min(finalHeight, 500)}px`;
      dropdown.style.overflowY = needsScroll ? 'auto' : 'visible';
      dropdown.style.maxHeight = `${finalHeight}px`;
      
      // Position directly below (or above) the button, left-aligned - as in reference image
      const left = Math.min(
        buttonRect.left,
        window.innerWidth - dropdown.offsetWidth - 20,
      );
      const top = shouldOpenUpward
        ? buttonRect.top - finalHeight - dropDownPadding
        : buttonRect.bottom + dropDownPadding;
      
      dropdown.style.top = `${top}px`;
      dropdown.style.left = `${left}px`;
      
      // Add classes to indicate direction and scroll state for styling purposes
      dropdown.classList.toggle('dropdown--upward', shouldOpenUpward);
      dropdown.classList.toggle('dropdown--scrollable', needsScroll);
    }
  }, []);

  useEffect(() => {
    if (showDropDown) {
      // Initial position setup
      updateDropdownPosition();
      
      // Also update position after a short delay to handle cases where
      // the dropdown content might affect its height after initial render
      const timeoutId = setTimeout(updateDropdownPosition, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [showDropDown, updateDropdownPosition]);

  useEffect(() => {
    const button = buttonRef.current;

    if (button !== null && showDropDown) {
      const handle = (event) => {
        const target = event.target;
        if (!isDOMNode(target)) {
          return;
        }
        if (stopCloseOnClickSelf) {
          if (dropDownRef.current && dropDownRef.current.contains(target)) {
            return;
          }
        }
        if (!button.contains(target)) {
          setShowDropDown(false);
        }
      };
      document.addEventListener('click', handle);

      return () => {
        document.removeEventListener('click', handle);
      };
    }
  }, [dropDownRef, buttonRef, showDropDown, stopCloseOnClickSelf]);

  // Effect to handle body scroll prevention with reference counting
  // Only when preventBodyScroll=true (e.g. modals); toolbar dropdowns keep scrollbar visible
  useEffect(() => {
    if (showDropDown && preventBodyScroll) {
      openDropdownCount++;

      if (openDropdownCount === 1) {
        originalBodyOverflow = document.body.style.overflow || 'auto';
        document.body.style.overflow = 'hidden';
      }

      return () => {
        openDropdownCount--;
        if (openDropdownCount === 0) {
          document.body.style.overflow = originalBodyOverflow;
        }
      };
    }
  }, [showDropDown, preventBodyScroll]);

  useEffect(() => {
    const handleButtonPositionUpdate = () => {
      if (showDropDown) {
        updateDropdownPosition();
      }
    };

    if (showDropDown) {
      document.addEventListener('scroll', handleButtonPositionUpdate);
      document.addEventListener('resize', handleButtonPositionUpdate);

      return () => {
        document.removeEventListener('scroll', handleButtonPositionUpdate);
        document.removeEventListener('resize', handleButtonPositionUpdate);
      };
    }
  }, [showDropDown, updateDropdownPosition]);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-label={buttonAriaLabel || buttonLabel}
        className={buttonClassName}
        onClick={() => setShowDropDown(!showDropDown)}
        ref={buttonRef}>
        {buttonIconClassName && <span className={buttonIconClassName} />}
        {buttonLabel && (
          <span className="text dropdown-button-text">{buttonLabel}</span>
        )}
        <i className="chevron-down" />
      </button>

      {showDropDown &&
        createPortal(
          <DropDownItems dropDownRef={dropDownRef} onClose={handleClose}>
            {children}
          </DropDownItems>,
          document.body,
        )}
    </>
  );
}